import React, { useState, useRef, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import 'flag-icons/css/flag-icons.min.css';

import   styles from './CountrySelector.module.scss';
import   Icon   from '../images/Icon';

import { COUNTRY_LIST, COUNTRY_OTHER, Country } from '../../../dataTypes/adminManagment/countries';

const ALL_COUNTRIES: Country[] = [...COUNTRY_LIST, COUNTRY_OTHER];

// Comparaison insensible à la casse et aux accents (ex: "americ" trouve "Amérique").
const normalize = (value: string): string =>
  value.toLowerCase().normalize('NFD').replace(new RegExp('[\\u0300-\\u036f]', 'g'), '');

export interface CountrySelectorProps {
  value?       : string;
  onChange?    : (value: string) => void;
  disabled?    : boolean;
  width?       : string;
  height?      : string;
  placeholder? : string;
  className?   : string;
  // Entrée supplémentaire en tête de liste (ex: { value: '', label: 'Tous les pays' })
  // pour un usage en filtre, où l'on doit pouvoir revenir à "aucune sélection".
  allOption?   : { value: string; label: string };
}

// Marqueur interne distinguant la ligne "allOption" d'un vrai pays (aucun code ISO ne vaut ceci).
const ALL_OPTION_CODE = '__all__';

export const CountrySelector = ({
  value,
  onChange,
  disabled = false,
  width  = '100%',
  height = '26px',
  placeholder = 'Sélectionnez un pays',
  className,
  allOption,
}: CountrySelectorProps) => {

  const [ isOpen , setIsOpen ] = useState(false);
  const [ search , setSearch ] = useState('');

  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef     = useRef<HTMLInputElement>(null);

  const selectedCountry = allOption && value === allOption.value
    ? { code: '', name: allOption.label }
    : ALL_COUNTRIES.find((country) => country.name === value);

  const displayedCountries = allOption
    ? [ { code: ALL_OPTION_CODE, name: allOption.label } , ...ALL_COUNTRIES ]
    : ALL_COUNTRIES;

  const filteredCountries = useMemo(() => {
    if (search === '') return displayedCountries;
    const needle = normalize(search);
    return displayedCountries.filter((country) => normalize(country.name).includes(needle));
  }, [search, displayedCountries]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) searchRef.current?.focus();
  }, [isOpen]);

  const handleToggle = () => {
    if (disabled) return;
    setIsOpen((open) => !open);
    setSearch('');
  };

  const handleSelect = (country: Country) => {
    const newValue = country.code === ALL_OPTION_CODE ? (allOption?.value ?? '') : country.name;
    if (onChange) onChange(newValue);
    setIsOpen(false);
    setSearch('');
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
      setSearch('');
    }
  };

  return (
    <div ref={containerRef} className={classNames(styles.root, className)} style={{ width }}>

      <div className    = {classNames(styles.trigger, { [styles.trigger_disabled]: disabled })}
           style        = {{ height }}
           onClick      = {handleToggle}>
        {
         selectedCountry
         ? (
          <>
            {selectedCountry.code && <span className={classNames('fi', `fi-${selectedCountry.code}`, styles.flag)} />}
            <span className={classNames(styles.label)}>{selectedCountry.name}</span>
          </>
         )
         : <span className={classNames(styles.placeholder)}>{placeholder}</span>
        }
        <Icon name="ChevronDown" color="var(--color-Select-FG-normal)" width={16} height={16} />
      </div>

      {
       isOpen &&
       (
        <div className={classNames(styles.panel)}>
          <input ref         = {searchRef}
                 type        = "text"
                 className   = {classNames(styles.search)}
                 placeholder = "Rechercher…"
                 value       = {search}
                 onChange    = {(e) => setSearch(e.target.value)}
                 onKeyDown   = {handleSearchKeyDown} />

          <div className={classNames(styles.list)}>
            {
             filteredCountries.map((country) => {
              const isAllOption = country.code === ALL_OPTION_CODE;
              const isSelected  = isAllOption ? value === allOption?.value : country.name === value;
              return (
               <div key       = {country.code || 'other'}
                    className = {classNames(styles.option, { [styles.option_selected]: isSelected })}
                    onClick   = {() => handleSelect(country)}>
                 {country.code && !isAllOption && <span className={classNames('fi', `fi-${country.code}`, styles.flag)} />}
                 <span>{country.name}</span>
               </div>
              );
             })
            }
            {
             filteredCountries.length === 0 &&
             <div className={classNames(styles.empty)}>Aucun résultat</div>
            }
          </div>
        </div>
       )
      }

    </div>
  );
};

export default CountrySelector;
